import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import styles from './VideoAnalyzer.module.css';
import { Maximize2, Minimize2 } from 'lucide-react';
import MetricsOverlay from './components/MetricsOverlay';
import SubtitleOverlay from './components/SubtitleOverlay';
import FullscreenControls from './components/FullscreenControls';
import CoachCharacter from './components/CoachCharacter';
import CoachFeedback from './components/CoachFeedback';
import CoachImprovementTracker from './components/CoachImprovementTracker';
import CoachIntro from './components/CoachIntro';
import useFullscreen from './hooks/useFullscreen';
import useMediaPipeModels from './hooks/useMediaPipeModels';
import useVideoAnalysis from './hooks/useVideoAnalysis';
import Notes from './components/Notes';

const VideoAnalyzer = ({
    videoStream,
    isRecording,
    onAnalysisUpdate,
    transcriptSegments,
    partialTranscript,
    recordingTime,
    timeLimit,
    onStopRecording,
    onSaveRecording,
    processingState
}) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [hasRecordedBefore, setHasRecordedBefore] = useState(false);
    const [activeIntro, setActiveIntro] = useState(false);
    const [currentMood, setCurrentMood] = useState('neutral');
    const [isMobileView, setIsMobileView] = useState(false);

    // Fullscreen handling
    const {
        isFullscreen,
        // eslint-disable-next-line
        fullscreenError,
        handleEnterFullscreen,
        handleExitFullscreen
    } = useFullscreen(containerRef);

    // Check if the screen is mobile size
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobileView(window.innerWidth <= 768);
        };

        // Initial check
        checkScreenSize();

        // Event listener for window resize
        window.addEventListener('resize', checkScreenSize);

        // Cleanup
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // MediaPipe models loading with performance optimizations
    const {
        faceLandmarker,
        poseLandmarker,
        handLandmarker,
        modelsLoading,
        loadingProgress
    } = useMediaPipeModels({
        faceDetectionConfidence: 0.5, // Slightly lower threshold for better performance
        poseDetectionConfidence: 0.5,
        handDetectionConfidence: 0.5,
        trackingConfidence: 0.5,
        useLiteModels: true, // Always use lite models
        processingFrequency: 8, // Process every 8th frame
        useGPU: true // Use GPU acceleration when available
    });

    // Analysis state and logic with performance optimizations
    const {
        facialEngagement,
        handsVisible,
        goodPosture,
        facialEngagementProgress,
        handsProgress,
        postureProgress
    } = useVideoAnalysis({
        videoRef,
        isRecording,
        faceLandmarker,
        poseLandmarker,
        handLandmarker,
        onAnalysisUpdate,
        processingFrequency: 8 // Process every 8th frame
    });

    // Memoize handlers to prevent unnecessary re-renders
    const handleActiveIntroChange = useCallback((isActive) => {
        setActiveIntro(isActive);
    }, []);

    const handleFeedbackChange = useCallback((mood) => {
        setCurrentMood(mood);
    }, []);

    // Toggle fullscreen function
    const toggleFullscreen = useCallback(() => {
        if (isFullscreen) {
            handleExitFullscreen();
        } else {
            handleEnterFullscreen();
        }
    }, [isFullscreen, handleEnterFullscreen, handleExitFullscreen]);

    // Enter fullscreen when recording starts
    useEffect(() => {
        if (isRecording && !isFullscreen) {
            handleEnterFullscreen();
        }

        // Track if user has recorded before, but delay setting it
        // to allow intro animation to play completely
        if (isRecording && !hasRecordedBefore) {
            // Wait for intro animation to complete (12 seconds total - matches the 3 steps at 4s each)
            const introCompletionDelay = setTimeout(() => {
                setHasRecordedBefore(true);
            }, 6000); // 6 seconds = INTRO_DELAY (500) + STEP_DURATION (2000) * 3 steps

            // Cleanup the timeout if component unmounts or recording stops
            return () => clearTimeout(introCompletionDelay);
        }
        // eslint-disable-next-line 
    }, [isRecording]);

    // Set up video stream with performance optimizations
    useEffect(() => {
        if (!videoStream || !videoRef.current) return;

        const video = videoRef.current;
        video.srcObject = videoStream;

        // Apply performance optimizations for video rendering
        video.playsInline = true;
        video.muted = true;
        video.autoPlay = true;

        // Use low resolution for processing
        if (video.srcObject) {
            const videoTrack = video.srcObject.getVideoTracks()[0];
            if (videoTrack) {
                try {
                    // Limit frame rate of video track
                    const capabilities = videoTrack.getCapabilities();
                    if (capabilities.frameRate) {
                        videoTrack.applyConstraints({
                            frameRate: 24 // Limit to 24fps for better performance
                        }).catch(err => console.error('Error applying constraints:', err));
                    }
                } catch (err) {
                    console.error('Error optimizing video track:', err);
                }
            }
        }

        // Play video with error handling
        video.play().catch(err => {
            console.error('Error playing video:', err);
        });
    }, [videoStream]);

    // Determine if coaching elements should be visible
    const shouldShowCoachingElements = useMemo(() => {
        // Always show on large screens
        if (!isMobileView) return true;

        // On mobile, only show in fullscreen
        return isFullscreen;
    }, [isMobileView, isFullscreen]);

    // Memoize components to prevent unnecessary re-renders
    const metricsOverlay = useMemo(() => (
        <MetricsOverlay
            modelsLoading={modelsLoading}
            loadingProgress={loadingProgress}
            facialEngagement={facialEngagement}
            handsVisible={handsVisible}
            goodPosture={goodPosture}
            facialEngagementProgress={facialEngagementProgress}
            handsProgress={handsProgress}
            postureProgress={postureProgress}
        />
    ), [modelsLoading, loadingProgress, facialEngagement, handsVisible, goodPosture,
        facialEngagementProgress, handsProgress, postureProgress]);

    const subtitleOverlay = useMemo(() => (
        <SubtitleOverlay
            transcriptSegments={transcriptSegments}
            partialTranscript={partialTranscript}
        />
    ), [transcriptSegments, partialTranscript]);

    // Memoized video style for better performance
    const videoStyle = useMemo(() => ({
        transform: 'translate3d(0,0,0)',
        backfaceVisibility: 'hidden',
        perspective: 1000,
        willChange: 'transform', // Hint to browser for optimization
        imageRendering: 'optimizeSpeed' // Optimize for speed over quality
    }), []);

    return (
        <div ref={containerRef} className={`${styles.analyzerContainer} ${isFullscreen ? styles.fullscreen : ''}`}>
            {/* Fullscreen toggle button */}
            <button
                className={styles.fullscreenToggle}
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
                {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
            </button>

            <div className={styles.videoSection}>
                <video
                    ref={videoRef}
                    className={styles.videoElement}
                    autoPlay
                    playsInline
                    muted
                    style={videoStyle}
                />

                {metricsOverlay}
                {subtitleOverlay}

                {/* Render components conditionally based on screen size and fullscreen state */}
                {isRecording && shouldShowCoachingElements && (
                    <>
                        <CoachCharacter
                            facialEngagement={facialEngagement}
                            handsVisible={handsVisible}
                            goodPosture={goodPosture}
                            isRecording={isRecording}
                            isFirstRecording={!hasRecordedBefore}
                            activeIntro={activeIntro}
                            mood={currentMood}
                        />

                        <CoachFeedback
                            facialEngagement={facialEngagement}
                            handsVisible={handsVisible}
                            goodPosture={goodPosture}
                            isRecording={isRecording}
                            activeIntro={activeIntro}
                            onFeedbackChange={handleFeedbackChange}
                        />

                        <CoachImprovementTracker
                            facialEngagement={facialEngagement}
                            handsVisible={handsVisible}
                            goodPosture={goodPosture}
                            isRecording={isRecording}
                        />

                        <CoachIntro
                            isRecording={isRecording}
                            isFirstRecording={!hasRecordedBefore}
                            onActiveIntroChange={handleActiveIntroChange}
                        />

                        <Notes />
                    </>
                )}

                {isFullscreen && (
                    <FullscreenControls
                        recordingTime={recordingTime}
                        timeLimit={timeLimit}
                        isRecording={isRecording}
                        processingState={processingState}
                        onStopRecording={onStopRecording}
                        onSaveRecording={onSaveRecording}
                    />
                )}
            </div>
        </div>
    );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(VideoAnalyzer);